import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileUp,
  History,
  Loader2,
  Pencil,
  Plus,
  Rocket,
  Search,
  Trash2,
  Workflow,
  Zap
} from "lucide-react";
import {
  commitImport,
  createEndpoint,
  decommissionInHouse,
  deleteEndpoint,
  disableInHouse,
  getAuditTrail,
  getDecommissionImpact,
  previewImport,
  publishEndpoint,
  publishInHouse,
  unpublishEndpoint,
  unpublishInHouse,
  updateEndpoint
} from "../../services/inHouseService";
import {
  Card,
  ErrorNote,
  Field,
  MethodPill,
  StatusBadge,
  StepFooter,
  inputClass,
  checkPath,
  fileProblem,
  pathPlaceholders,
  statusOf
} from "./parts";
import OrbitLoader from "../ui/OrbitLoader";
import ResponseViewer from "./ResponseViewer";
import { LIFECYCLE, allowedEvents, nextState, reportRefused } from "./machines";
import { triedCallIds } from "./tryMemory";

/** Steps 3 (Calls) and 5 (Go live), and the History panel. Step 4 (Try it) is in TryPanel. */

const LOCATION_LABEL = {
  path: "In the URL path",
  query: "In the URL query",
  header: "Header",
  body: "Request body"
};

/* ------------------------------------------------------------------ */
/* Step 3 — calls                                                      */
/* ------------------------------------------------------------------ */

const SEARCH_FROM = 6; // a short list doesn't need a search box
const FOLD_FROM = 20;

/** Calls are grouped by the first part of their path, the way Postman folders a collection. */
export const groupOf = (path) => (path || "").split("/").find((part) => part && !part.startsWith("{")) || "";

export function groupCalls(endpoints) {
  const groups = new Map();
  endpoints.forEach((e) => {
    const name = groupOf(e.path);
    groups.set(name, [...(groups.get(name) || []), e]);
  });
  return [...groups.entries()];
}

const matches = (e, q) =>
  !q || e.name.toLowerCase().includes(q) || e.path.toLowerCase().includes(q) || e.http_method.toLowerCase() === q;

export function CallsStep({ integration, refresh, pendingImport, setPendingImport, clearPendingImport, devMode, onBack, onNext }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // endpoint being edited
  const [reimporting, setReimporting] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [blocked, setBlocked] = useState(null); // { endpoint, dependents }
  const [query, setQuery] = useState("");
  // A long collection opens folded except its first group, so the page starts as an outline.
  const [collapsed, setCollapsed] = useState(() =>
    integration.endpoints.length > FOLD_FROM ? new Set(groupCalls(integration.endpoints).slice(1).map(([name]) => name)) : new Set()
  );
  const endpoints = integration.endpoints;
  const live = integration.status !== "draft";
  const q = query.trim().toLowerCase();
  const groups = useMemo(
    () => groupCalls(endpoints).map(([name, calls]) => [name, calls.filter((e) => matches(e, q))]).filter(([, calls]) => calls.length),
    [endpoints, q]
  );
  const toggleGroup = (name) =>
    setCollapsed((c) => {
      const next = new Set(c);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const act = async (endpoint, request) => {
    setBusyId(endpoint.id);
    setError("");
    const result = await request();
    setBusyId(null);
    if (result.success) {
      setBlocked(null);
      await refresh();
    } else if (result.status === 409 && result.dependents) {
      setBlocked({ endpoint, dependents: result.dependents });
    } else setError(result.message);
  };

  const remove = (endpoint) => {
    // Deleting can't be undone from the UI; ask first.
    if (!window.confirm(`Delete “${endpoint.name}”? Its saved test inputs are deleted too.`)) return;
    act(endpoint, () => deleteEndpoint(integration.id, endpoint.id));
  };
  const publish = (endpoint) => act(endpoint, () => publishEndpoint(integration.id, endpoint.id));
  const unpublish = (endpoint, confirm = false) => {
    if (!confirm &&
        !window.confirm(`Unpublish “${endpoint.name}”? It goes back to a draft and your team can no longer use it.`)) {
      return;
    }
    act(endpoint, () => unpublishEndpoint(integration.id, endpoint.id, confirm));
  };

  return (
    <>
      <div className="space-y-6">
        {pendingImport && (
          <ImportPicker
            key={pendingImport.preview.sha256}
            integrationId={integration.id}
            pending={pendingImport}
            devMode={devMode}
            onDone={async () => {
              clearPendingImport();
              await refresh();
            }}
            onCancel={clearPendingImport}
            onRefresh={refresh}
          />
        )}

        <Card
          title="Calls Orbit can make"
          subtitle={
            endpoints.length
              ? `${endpoints.length} call${endpoints.length === 1 ? "" : "s"}`
              : "None yet — import them from your docs or add one by hand."
          }
          action={
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setReimporting(true)}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                <FileUp className="h-4 w-4" /> Import
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setAdding(true);
                }}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                <Plus className="h-4 w-4" /> Add a call
              </button>
            </div>
          }
        >
          {reimporting && (
            <ReimportBox
              integrationId={integration.id}
              onPreview={(pending) => {
                setPendingImport(pending);
                setReimporting(false);
              }}
              onCancel={() => setReimporting(false)}
            />
          )}
          {endpoints.length >= SEARCH_FROM && (
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter by name, path or method"
                aria-label="Filter calls"
                className={`${inputClass(false)} py-2 pl-9`}
              />
            </div>
          )}
          {endpoints.length > 0 && groups.length === 0 && (
            <p className="py-6 text-center text-sm text-gray-500">Nothing matches “{query.trim()}”.</p>
          )}
          {groups.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-gray-200">
              {groups.map(([name, calls]) => {
                // A search shows every match, folded or not.
                const open = !!q || groups.length === 1 || !collapsed.has(name);
                return (
                  <section key={name} aria-label={name ? `/${name}` : "Other calls"} className="border-b border-gray-200 last:border-b-0">
                    {groups.length > 1 && (
                      <button
                        type="button"
                        onClick={() => toggleGroup(name)}
                        aria-expanded={open}
                        className="flex w-full items-center gap-2 bg-gray-50 px-4 py-2 text-left text-xs font-medium text-gray-600 hover:bg-gray-100"
                      >
                        <ChevronDown className={`h-3.5 w-3.5 transition ${open ? "" : "-rotate-90"}`} />
                        <span className="font-mono">{name ? `/${name}` : "Other"}</span>
                        <span className="rounded-full bg-white px-1.5 text-gray-500 ring-1 ring-inset ring-gray-200">{calls.length}</span>
                      </button>
                    )}
                    {open && (
                      <div className="divide-y divide-gray-100">
                        {calls.map((e) => (
                          <EndpointRow
                            key={e.id}
                            endpoint={e}
                            devMode={devMode}
                            live={live}
                            busy={busyId === e.id}
                            onEdit={() => {
                              setAdding(false);
                              setEditing(e);
                            }}
                            onRemove={() => remove(e)}
                            onPublish={() => publish(e)}
                            onUnpublish={() => unpublish(e)}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
          {!live && endpoints.length > 0 && (
            <p className="mt-3 text-xs text-gray-500">Every call goes live together when you publish on Go live.</p>
          )}
          {live && endpoints.some((e) => e.lifecycle_state === "draft") && (
            <p className="mt-3 text-xs text-gray-500">
              Draft calls stay hidden from your team until you publish them.
            </p>
          )}
          {blocked && (
            <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50/60 p-4 text-sm">
              <p className="font-medium text-gray-900">“{blocked.endpoint.name}” is still used by:</p>
              <DependentsList dependents={blocked.dependents} endpoints={endpoints} />
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={() => unpublish(blocked.endpoint, true)} className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700">
                  Unpublish anyway
                </button>
                <button type="button" onClick={() => setBlocked(null)} className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100">
                  Keep it
                </button>
              </div>
            </div>
          )}
          <ErrorNote message={error} />
        </Card>

        {(adding || editing) && (
          <ManualEndpoint
            key={editing?.id || "new"}
            integrationId={integration.id}
            initial={editing}
            devMode={devMode}
            onCancel={() => {
              setAdding(false);
              setEditing(null);
            }}
            onAdded={async () => {
              setAdding(false);
              setEditing(null);
              await refresh();
            }}
          />
        )}
      </div>
      <StepFooter
        onBack={onBack}
        onNext={onNext}
        disabled={endpoints.length === 0}
        hint={endpoints.length ? null : pendingImport ? "Add the calls you picked first" : "Add at least one call first"}
      />
    </>
  );
}

function ReimportBox({ integrationId, onPreview, onCancel }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const read = async (source) => {
    setBusy(true);
    setError("");
    const result = await previewImport(integrationId, source);
    setBusy(false);
    if (result.success) onPreview({ source, preview: result.data });
    else setError(result.message);
  };

  const readFile = async (input) => {
    const file = input.files?.[0];
    input.value = ""; // so picking the same file again still fires
    if (!file) return;
    const problem = fileProblem(file);
    if (problem) {
      setError(problem);
      return;
    }
    const content = await file.text().catch(() => "");
    if (!content.trim()) setError("That file is empty or couldn't be opened.");
    else read({ content });
  };

  return (
    <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-4">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (url.trim() && !busy) read({ url: url.trim() });
        }}
      >
        <input
          autoFocus
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Link to your OpenAPI file"
          className={`${inputClass(false)} font-mono`}
        />
        <button
          type="submit"
          disabled={!url.trim() || busy}
          className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Read
        </button>
      </form>
      <div className="mt-2 flex items-center justify-between text-xs">
        <label className={`font-medium text-blue-600 hover:text-blue-700 ${busy ? "pointer-events-none opacity-50" : "cursor-pointer"}`}>
          {busy ? "Reading…" : "or upload a file (.json, .yaml)"}
          <input
            type="file"
            accept=".json,.yaml,.yml,application/json,application/yaml,text/yaml"
            className="hidden"
            disabled={busy}
            onChange={(e) => readFile(e.target)}
          />
        </label>
        <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-700">
          Cancel
        </button>
      </div>
      <ErrorNote message={error} />
    </div>
  );
}

/** Pick which calls to import. New calls start ticked; changed ones only update if ticked. */
function ImportPicker({ integrationId, pending, devMode, onDone, onCancel, onRefresh }) {
  const { preview, source } = pending;
  const usable = preview.operations.filter((o) => !o.skipped_reason && o.status !== "unchanged");
  const skipped = preview.operations.filter((o) => o.skipped_reason);
  const [picked, setPicked] = useState(
    () => new Set(usable.filter((o) => o.status === "new").map((o) => o.key))
  );
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const visible = usable.filter((o) =>
    `${o.name} ${o.path} ${o.method}`.toLowerCase().includes(q.toLowerCase())
  );
  const groups = useMemo(() => {
    const g = {};
    visible.forEach((o) => (g[o.tag || "Other"] ||= []).push(o));
    return g;
  }, [visible]);
  const allOn = visible.length > 0 && visible.every((o) => picked.has(o.key));

  const toggle = (key) =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const commit = async () => {
    setBusy(true);
    setError("");
    const select = [...picked];
    const changed = new Set(usable.filter((o) => o.status === "changed").map((o) => o.key));
    const result = await commitImport(integrationId, {
      ...source,
      sha256: preview.sha256,
      select,
      update_existing: select.filter((k) => changed.has(k))
    });
    setBusy(false);
    // An add the browser gave up on can still finish on the server, so a retry finds the calls
    // already there: that is done too. On any other failure, reload to show what did land.
    if (result.success || (result.status === 409 && /already here/.test(result.message || ""))) onDone();
    else {
      setError(result.message);
      onRefresh();
    }
  };

  return (
    <Card
      title="Which calls should Orbit be able to make?"
      subtitle={`${picked.size} of ${usable.length} selected${preview.title ? ` from “${preview.title}”` : ""}`}
    >
      {devMode && preview.warnings.length > 0 && (
        <ul className="mb-4 list-disc space-y-0.5 rounded-xl bg-amber-50 py-3 pl-8 pr-4 text-xs text-amber-900">
          {preview.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
      {usable.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">
          Everything in this file is already here and unchanged.
        </p>
      ) : (
        <>
          {usable.length > 4 && (
            <div className="mb-4 flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search calls" className={`${inputClass(false)} pl-9`} />
              </div>
              <button
                type="button"
                onClick={() =>
                  setPicked((p) => {
                    const next = new Set(p);
                    visible.forEach((o) => (allOn ? next.delete(o.key) : next.add(o.key)));
                    return next;
                  })
                }
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                {allOn ? "Clear all" : "Select all"}
              </button>
            </div>
          )}
          <div className="max-h-[420px] space-y-5 overflow-y-auto">
            {Object.entries(groups).map(([tag, list]) => (
              <div key={tag}>
                <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-gray-500">{tag}</p>
                <div className="divide-y divide-gray-100 rounded-xl border border-gray-200">
                  {list.map((o) => (
                    <label key={o.key} className="flex cursor-pointer items-center gap-3 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={picked.has(o.key)}
                        onChange={() => toggle(o.key)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <MethodPill method={o.method} />
                      <span className="text-sm font-medium text-gray-900">{o.name}</span>
                      <span className="truncate font-mono text-xs text-gray-500">{o.path}</span>
                      {o.status === "changed" && (
                        <span className="ml-auto rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                          Changed — tick to update
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {skipped.length > 0 && (
        <details className="mt-5 text-sm">
          <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
            {skipped.length} call{skipped.length > 1 ? "s" : ""} can't be imported yet
          </summary>
          <ul className="mt-2 space-y-2">
            {skipped.map((o) => (
              <li key={o.key} className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2 text-gray-500">
                <MethodPill method={o.method} className="opacity-60" />
                <span className="font-mono text-xs">{o.path}</span>
                <span className="ml-auto text-xs">{o.skipped_reason}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {preview.removed.length > 0 && (
        <p className="mt-4 text-xs text-gray-500">
          {preview.removed.length} call{preview.removed.length > 1 ? "s are" : " is"} no longer in the file. Nothing is
          deleted automatically.
        </p>
      )}
      <ErrorNote message={error} />
      <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5">
        <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">
          Not now
        </button>
        <button
          type="button"
          disabled={picked.size === 0 || busy}
          onClick={commit}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Add {picked.size} call{picked.size === 1 ? "" : "s"}
        </button>
      </div>
    </Card>
  );
}

function EndpointRow({ endpoint, devMode, live, busy, onEdit, onRemove, onPublish, onUnpublish }) {
  const [open, setOpen] = useState(false);
  const draft = endpoint.lifecycle_state === "draft";
  const iconButton = "rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-40";
  return (
    <div>
      <div className="group flex items-center gap-3 px-4 py-2 hover:bg-gray-50">
        <MethodPill method={endpoint.http_method} className="shrink-0" />
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <span className="truncate text-sm font-medium text-gray-900">{endpoint.name}</span>
          <span className="hidden truncate font-mono text-xs text-gray-500 sm:inline">{endpoint.path}</span>
          <ChevronRight className={`ml-auto h-4 w-4 shrink-0 text-gray-400 transition ${open ? "rotate-90" : ""}`} />
        </button>
        {/* Before going live every call is a draft, so the badge would only repeat itself. */}
        {live && <StatusBadge status={statusOf(endpoint)} />}
        {busy && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
        {live && (
          <button
            type="button"
            disabled={busy}
            onClick={draft ? onPublish : onUnpublish}
            className="rounded-md px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-40"
          >
            {draft ? "Publish" : "Unpublish"}
          </button>
        )}
        {/* Edit and delete show on hover or keyboard focus, and always where there is no hover. */}
        <div className="flex items-center gap-1 opacity-0 transition group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
          <button type="button" disabled={busy} aria-label={`Edit ${endpoint.name}`} title="Edit" onClick={onEdit} className={`${iconButton} hover:text-gray-700`}>
            <Pencil className="h-4 w-4" />
          </button>
          {draft && (
            <button type="button" disabled={busy} aria-label={`Delete ${endpoint.name}`} title="Delete" onClick={onRemove} className={`${iconButton} hover:text-rose-600`}>
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-4">
          {endpoint.description && <p className="mb-3 text-sm text-gray-600">{endpoint.description}</p>}
          {endpoint.fields.length === 0 ? (
            <p className="text-sm text-gray-500">This call needs no input.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className="pb-2 font-medium">Field</th>
                  <th className="pb-2 font-medium">{devMode ? "Location · type" : "Required"}</th>
                  <th className="pb-2 font-medium">Description</th>
                </tr>
              </thead>
              <tbody>
                {endpoint.fields.map((f) => (
                  <tr key={f.key} className="align-top">
                    <td className="py-1.5 pr-4 font-mono text-xs text-gray-900">{devMode ? f.key : f.name}</td>
                    <td className="py-1.5 pr-4 text-xs text-gray-600">
                      {devMode ? `${f.location} · ${f.type}${f.required ? " · required" : ""}` : f.required ? "Required" : "Optional"}
                    </td>
                    <td className="py-1.5 text-xs text-gray-600">
                      {f.description}
                      {f.enum && <span className="block text-gray-400">One of: {f.enum.map(String).join(", ")}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const FIELD_TYPES = ["string", "number", "integer", "boolean", "date", "object", "array"];

const blankField = (name = "", location = "query") => ({
  name,
  location,
  type: "string",
  required: location === "path",
  description: "",
  example: ""
});

/** Stored fields (from fields_from_schema) into editable rows; examples become text. */
const toRow = (f) => ({
  name: f.name,
  location: f.location,
  type: f.type || "string",
  required: !!f.required || f.location === "path",
  description: f.description || "",
  example: f.example === null || f.example === undefined ? "" : typeof f.example === "object" ? JSON.stringify(f.example) : String(f.example),
  enum: f.enum || null
});

/** Text examples back to the field's type, so a number stays a number in the schema. */
const exampleValue = (f) => {
  if (f.example === "") return null;
  if (f.type === "number" || f.type === "integer") {
    const n = Number(f.example);
    return Number.isNaN(n) ? f.example : n;
  }
  if (f.type === "boolean") return f.example === "true" ? true : f.example === "false" ? false : f.example;
  if (f.type === "object" || f.type === "array") {
    try {
      return JSON.parse(f.example);
    } catch {
      return f.example;
    }
  }
  return f.example;
};

const FIELD_NAME = /^[A-Za-z0-9_$][A-Za-z0-9_$.\-[\]]{0,127}$/;
const HEADER_NAME = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]{1,128}$/;

const fieldProblem = (f, all) => {
  if (!f.name) return null;
  const ok = (f.location === "header" ? HEADER_NAME : FIELD_NAME).test(f.name);
  if (!ok) return "Letters, numbers, - and _ only";
  const same = all.filter((g) => g.location === f.location && (f.location === "header" ? g.name.toLowerCase() === f.name.toLowerCase() : g.name === f.name));
  return same.length > 1 ? "Listed twice" : null;
};

/** Add a call, or edit one when `initial` is given (same form, so both behave alike). */
function ManualEndpoint({ integrationId, initial, onAdded, onCancel, devMode }) {
  const [method, setMethod] = useState(initial?.http_method || "GET");
  const [path, setPath] = useState(initial?.path || "");
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [fields, setFields] = useState(() => (initial?.fields || []).map(toRow));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // One path field per {placeholder}, kept in step with what the admin types.
  const placeholders = [...new Set(pathPlaceholders(path))];
  const pathFields = placeholders.map(
    (n) => fields.find((f) => f.location === "path" && f.name === n) || blankField(n, "path")
  );
  const all = [...pathFields, ...fields.filter((f) => f.location !== "path")];
  const pathError = checkPath(path);
  const problems = all.map((f) => fieldProblem(f, all));
  const setField = (i, patch) => setFields(all.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const live = initial && initial.lifecycle_state === "published";

  const save = async () => {
    setBusy(true);
    setError("");
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      http_method: method,
      path: path.trim(),
      fields: all
        .filter((f) => f.name)
        .map((f) => ({
          name: f.name,
          location: f.location,
          type: f.type,
          required: f.required,
          description: f.description || null,
          example: exampleValue(f),
          enum: f.enum || null
        }))
    };
    const result = initial
      ? await updateEndpoint(integrationId, initial.id, body)
      : await createEndpoint(integrationId, body);
    setBusy(false);
    if (result.success) onAdded();
    else setError(result.message);
  };

  const invalid = !name.trim() || !path.trim() || !!pathError || problems.some(Boolean);

  return (
    <Card
      title={initial ? `Edit “${initial.name}”` : "Add a call"}
      subtitle={
        live
          ? "This call is live: changes apply as soon as you save, and History keeps the previous version."
          : "Copy these from your API docs. Orbit fills in the address and sign-in for you."
      }
    >
      <div className="space-y-5">
        <Field label="What does it do?" hint="Shown to your team, e.g. “Look up a claim”.">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={200} placeholder="Look up a claim" className={inputClass(false)} />
        </Field>
        <Field
          label="Method and path"
          error={pathError}
          hint={
            placeholders.length
              ? `Found ${placeholders.map((p) => `{${p}}`).join(", ")} — added below as required fields.`
              : "Use {curly braces} for parts that change, e.g. /claims/{claimNumber}"
          }
        >
          <div className="flex gap-2">
            <div className="w-32 shrink-0">
              <select value={method} onChange={(e) => setMethod(e.target.value)} aria-label="Method" className={`${inputClass(false)} font-mono`}>
                {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </div>
            <input value={path} onChange={(e) => setPath(e.target.value)} placeholder="/claims/{claimNumber}" aria-label="Path" className={`${inputClass(!!pathError)} font-mono`} />
          </div>
        </Field>
        <Field label="Description" optional hint="The AI builder reads this to decide when to use the call.">
          <textarea rows={2} maxLength={4000} value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass(false)} />
        </Field>

        <div>
          <p className="mb-2 text-sm font-medium text-gray-900">Inputs</p>
          {all.length === 0 && <p className="mb-2 text-sm text-gray-500">None yet.</p>}
          <div className="space-y-3">
            {all.map((f, i) => (
              <div key={`${f.location}-${i}`} className="rounded-xl border border-gray-200 p-3">
                <div className="grid grid-cols-12 gap-2">
                  <input value={f.name} disabled={f.location === "path"} onChange={(e) => setField(i, { name: e.target.value.trim() })} placeholder="name" aria-label="Input name" className={`${inputClass(!!problems[i])} col-span-3 font-mono disabled:bg-gray-50`} />
                  <select value={f.location} disabled={f.location === "path"} onChange={(e) => setField(i, { location: e.target.value })} aria-label="Where it goes" className={`${inputClass(false)} col-span-3 disabled:bg-gray-50`}>
                    {Object.entries(LOCATION_LABEL)
                      .filter(([k]) => k !== "path" || f.location === "path")
                      .map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                  </select>
                  <select value={f.type} onChange={(e) => setField(i, { type: e.target.value })} aria-label="Type" className={`${inputClass(false)} col-span-2`}>
                    {FIELD_TYPES.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                  <input value={f.example} onChange={(e) => setField(i, { example: e.target.value })} placeholder="example" aria-label="Example" className={`${inputClass(false)} col-span-2`} />
                  <label className="col-span-1 flex items-center justify-center text-xs text-gray-600">
                    <input type="checkbox" checked={f.required} disabled={f.location === "path"} onChange={(e) => setField(i, { required: e.target.checked })} className="mr-1 rounded border-gray-300 text-blue-600" />
                    Req
                  </label>
                  <button type="button" aria-label="Remove input" disabled={f.location === "path"} onClick={() => setFields(all.filter((_, j) => j !== i))} className="col-span-1 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-rose-600 disabled:invisible">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {problems[i] && <p className="mt-1 text-xs text-rose-600">{problems[i]}</p>}
                {(devMode || f.description) && (
                  <input value={f.description} onChange={(e) => setField(i, { description: e.target.value })} placeholder="Description (optional)" aria-label="Input description" maxLength={1000} className={`${inputClass(false)} mt-2`} />
                )}
                {f.enum && <p className="mt-1 text-xs text-gray-500">One of: {f.enum.map(String).join(", ")}</p>}
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setFields([...all, blankField("", method === "GET" || method === "DELETE" ? "query" : "body")])} className="mt-2 flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
            <Plus className="h-4 w-4" /> Add input
          </button>
        </div>
        <ErrorNote message={error} />
        <div className="flex justify-end gap-2 border-t border-gray-100 pt-5">
          <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">Cancel</button>
          <button type="button" disabled={invalid || busy} onClick={save} className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {initial ? "Save changes" : "Add call"}
          </button>
        </div>
      </div>
    </Card>
  );
}

/** Inputs are text; the API expects numbers, booleans and JSON where the schema says so. */
/* ------------------------------------------------------------------ */
/* Step 5 — go live                                                    */
/* ------------------------------------------------------------------ */

const SURFACES = [
  { icon: Zap, title: "Event routing", body: "Send LexAI events (e.g. “quote declined”) straight to a call." },
  { icon: Workflow, title: "Workflows", body: "Use any call as a step in a workflow." },
  { icon: Bot, title: "AI builder & Ask Orbit", body: "Describe what you want in plain English; Orbit picks the call." }
];

// The API call behind each lifecycle event. Turning a disabled integration back on is a publish.
const LIFECYCLE_CALLS = { publish: publishInHouse, turnOn: publishInHouse, unpublish: unpublishInHouse, disable: disableInHouse };

/** Names what would stop working, so "continue anyway" is an informed choice. */
function DependentsList({ dependents, endpoints }) {
  const callName = (id) => endpoints.find((e) => String(e.id) === String(id))?.name;
  return (
    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-800">
      {dependents.workflows.map((w) => (
        <li key={`w-${w.id}`}>Workflow “{w.name}”</li>
      ))}
      {dependents.routes.map((r) => (
        <li key={`r-${r.id}`}>An event route{callName(r.endpoint_id) ? ` to “${callName(r.endpoint_id)}”` : ""}</li>
      ))}
    </ul>
  );
}

const UNPUBLISH_WARNING = "Unpublish? It goes back to a draft and your team can no longer use any of its calls.";

/**
 * Publish, unpublish and disable in one place, so the setup and manage screens confirm and
 * report the same way. A 409 with dependents means something still uses it; `blocked` holds
 * them until the admin continues or keeps it. `onMoved(action)` runs once the server has
 * accepted a move and before the reload, so a screen change can't race the new status.
 */
export function useLifecycle(integration, refresh, onMoved) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState(null); // { action, dependents }

  const run = async (action, confirm = false) => {
    const from = statusOf(integration);
    if (busy) return false;
    if (nextState(LIFECYCLE, from, action) === undefined) {
      reportRefused("lifecycle", from, action);
      return false;
    }
    if (action === "unpublish" && !confirm && !window.confirm(UNPUBLISH_WARNING)) return false;
    setBusy(true);
    setError("");
    const result = await LIFECYCLE_CALLS[action](integration.id, confirm);
    setBusy(false);
    if (result.success) {
      setBlocked(null);
      onMoved?.(action);
      await refresh();
      return true;
    }
    if (result.status === 409 && result.dependents) setBlocked({ action, dependents: result.dependents });
    else setError(result.message);
    return false;
  };

  return { busy, error, blocked, run, keepIt: () => setBlocked(null) };
}

function BlockedCard({ integration, lifecycle }) {
  const { busy, blocked, run, keepIt } = lifecycle;
  if (!blocked) return null;
  return (
    <Card className="border-amber-300 bg-amber-50/40" title="These still use it" subtitle="They will stop working if you continue.">
      <DependentsList dependents={blocked.dependents} endpoints={integration.endpoints} />
      <div className="mt-4 flex gap-2">
        <button type="button" disabled={busy} onClick={() => run(blocked.action, true)} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50">
          {blocked.action === "disable" ? "Disable anyway" : "Unpublish anyway"}
        </button>
        <button type="button" onClick={keepIt} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">
          Keep it
        </button>
      </div>
    </Card>
  );
}

// How each lifecycle move looks as a button. Decommission has its own card.
const LIFECYCLE_BUTTONS = {
  turnOn: { label: "Turn back on", icon: Rocket, style: "bg-blue-600 text-white hover:bg-blue-700" },
  disable: { label: "Disable", style: "border border-gray-300 text-gray-800 hover:bg-gray-50" },
  unpublish: { label: "Unpublish", style: "border border-rose-200 text-rose-700 hover:bg-rose-50" }
};

/**
 * Turning a published integration off or back on. The buttons are the moves the LIFECYCLE
 * table allows from the current status, so the screen can't offer one the server would refuse.
 */
export function LifecycleCard({ integration, lifecycle }) {
  const { busy, error, run } = lifecycle;
  const status = statusOf(integration);
  const moves = allowedEvents(LIFECYCLE, status).filter((event) => LIFECYCLE_BUTTONS[event]);
  return (
    <>
      <Card
        title={status === "Live" ? "Turn it off" : "It's turned off"}
        subtitle="Disable stops every call but keeps everything published, so you can turn it back on. Unpublish returns it to a draft that only admins see."
      >
        <div className="flex gap-2">
          {moves.map((event) => {
            const { label, icon: Icon, style } = LIFECYCLE_BUTTONS[event];
            return (
              <button
                key={event}
                type="button"
                disabled={busy}
                onClick={() => run(event)}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 ${style}`}
              >
                {Icon && (busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />)}
                {label}
              </button>
            );
          })}
        </div>
      </Card>
      <BlockedCard integration={integration} lifecycle={lifecycle} />
      <ErrorNote message={error} />
    </>
  );
}

/** Your team's surfaces for a published call; shown before and after going live. */
export function SurfacesCard({ draft }) {
  return (
    <Card title={draft ? "Once live, your team can use it in" : "Your team can use it in"} subtitle={draft ? "Only admins can see it while it's a draft." : undefined}>
      <div className="grid gap-3 sm:grid-cols-3">
        {SURFACES.map(({ icon: Icon, title, body }) => (
          <div key={title} className="rounded-xl border border-gray-200 p-4">
            <Icon className="mb-3 h-5 w-5 text-blue-600" />
            <p className="text-sm font-medium text-gray-900">{title}</p>
            <p className="mt-1 text-xs text-gray-500">{body}</p>
          </div>
        ))}
      </div>
      <p className="mt-5 text-xs text-gray-500">Every change is recorded in History, so it can be undone by hand.</p>
    </Card>
  );
}

/** One line of the Go live checklist, with a way to the step that fixes it. */
function CheckItem({ ok, warn, text, note, fix, onFix }) {
  const Icon = ok ? CheckCircle2 : AlertTriangle;
  const tone = ok ? "text-emerald-600" : warn ? "text-amber-500" : "text-rose-500";
  return (
    <li className="flex items-center gap-3 px-6 py-3 text-sm">
      <Icon className={`h-5 w-5 shrink-0 ${tone}`} />
      <span className="min-w-0 flex-1 text-gray-800">
        {text}
        {note && <span className="ml-2 text-xs text-gray-500">{note}</span>}
      </span>
      {!ok && fix && (
        <button type="button" onClick={onFix} className="shrink-0 text-sm font-medium text-blue-600 hover:text-blue-700">
          {fix}
        </button>
      )}
    </li>
  );
}

/** Last setup step. Only drafts reach it: a published integration opens on its manage view. */
export function PublishStep({ integration, refresh, tested, onBack, onOpen, onPublished, onDecommissioned }) {
  const lifecycle = useLifecycle(integration, refresh, (action) => action === "publish" && onPublished());
  const count = integration.endpoints.length;
  const signedIn = !!integration.connection?.connected;
  // Tried in this visit, or sent from this browser before (Try it remembers each call it sends).
  const tried = useMemo(() => {
    const ids = new Set([...tested, ...triedCallIds(integration.id)]);
    return integration.endpoints.filter((e) => ids.has(e.id)).length;
  }, [tested, integration.id, integration.endpoints]);

  const checks = [
    { id: "api", ok: !!integration.api, text: integration.api ? "Address and sign-in set" : "Address and sign-in not set", fix: "Set them", step: "connection" },
    { id: "signin", ok: signedIn, text: signedIn ? "Sign-in details saved" : "Sign-in details not saved", fix: "Save them", step: "connection" },
    { id: "calls", ok: count > 0, text: count ? plural(count, "call") : "No calls yet", fix: "Add calls", step: "calls" },
    {
      id: "tried",
      ok: tried > 0,
      warn: true,
      text: `${tried} of ${plural(count, "call")} tried`,
      note: tried > 0 ? null : "Recommended, not required",
      fix: "Try a call",
      step: "test"
    }
  ];
  const canPublish = !!integration.api && count > 0 && signedIn;

  return (
    <>
      <div className="space-y-6">
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <h3 className="text-base font-semibold text-gray-900">Before it goes live</h3>
            <StatusBadge status="Draft" />
          </div>
          <ul className="divide-y divide-gray-100">
            {checks.map(({ id, step, ...check }) => (
              <CheckItem key={id} {...check} onFix={() => onOpen?.(step)} />
            ))}
          </ul>
        </section>
        <SurfacesCard draft />
        <ErrorNote message={lifecycle.error} />
      </div>
      <StepFooter
        onBack={onBack}
        onNext={() => lifecycle.run("publish")}
        nextLabel="Publish"
        icon={Rocket}
        busy={lifecycle.busy}
        disabled={!canPublish}
        hint={!canPublish ? "Finish the steps marked in red first" : undefined}
      />
      {/* Kept apart from Publish, as on Settings, so the permanent action is never next to the main one. */}
      <DangerZone>
        <DecommissionCard integration={integration} onDone={onDecommissioned} />
      </DangerZone>
    </>
  );
}

/** Actions that remove or turn things off, set apart below everything else. */
export function DangerZone({ children }) {
  return (
    <div className="mt-12 space-y-6 border-t border-gray-200 pt-6">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-rose-700">Danger zone</h2>
      {children}
    </div>
  );
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * Permanent removal. The impact is loaded from the server first, because the database
 * deletes routes and call history along with the calls; the admin types the name to confirm.
 */
export function DecommissionCard({ integration, onDone }) {
  const [open, setOpen] = useState(false);
  const [impact, setImpact] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const start = async () => {
    setOpen(true);
    setError("");
    setTyped("");
    setImpact(null);
    const result = await getDecommissionImpact(integration.id);
    if (result.success) setImpact(result.data);
    else setError(result.message);
  };

  const matches = typed.trim().replace(/\s+/g, " ").toLowerCase() === integration.name.toLowerCase();
  const breaks = impact && (impact.routes.length > 0 || impact.workflows.length > 0);

  const confirm = async () => {
    setBusy(true);
    setError("");
    const result = await decommissionInHouse(integration.id, typed, !!breaks);
    setBusy(false);
    if (result.success) onDone?.();
    else setError(result.message);
  };

  if (!open) {
    return (
      <Card title="Decommission" subtitle="Permanently remove this integration, its calls and its saved sign-in. This can't be undone.">
        <button type="button" onClick={start} className="rounded-lg border border-rose-300 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50">
          Decommission…
        </button>
      </Card>
    );
  }

  return (
    <Card className="border-rose-300" title={`Decommission “${integration.name}”?`} subtitle="This permanently removes it for everyone in your organization.">
      {!impact && !error && <OrbitLoader size={28} label="Checking what depends on it" />}
      {impact && (
        <>
          <p className="text-sm font-medium text-gray-900">This will delete:</p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-800">
            <li>{plural(impact.calls, "call")} and their saved test inputs</li>
            <li>The saved sign-in details</li>
            {impact.history.calls > 0 && <li>{plural(impact.history.calls, "logged call")} from its usage history</li>}
            {impact.history.tests > 0 && <li>{plural(impact.history.tests, "test run")}</li>}
            {impact.routes.length > 0 && (
              <li className="font-medium text-rose-700">{plural(impact.routes.length, "event route")} that send events to its calls</li>
            )}
          </ul>
          {impact.workflows.length > 0 && (
            <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50/60 p-3 text-sm">
              <p className="font-medium text-gray-900">These workflows will fail until you edit them:</p>
              <DependentsList dependents={{ workflows: impact.workflows, routes: [] }} endpoints={integration.endpoints} />
            </div>
          )}
          <p className="mt-4 text-xs text-gray-500">Its full definition stays in your audit trail, so it can be rebuilt by hand.</p>
          <Field label={`Type “${integration.name}” to confirm`}>
            <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} className={inputClass(false)} aria-label="Integration name" />
          </Field>
        </>
      )}
      <ErrorNote message={error} />
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">
          Keep it
        </button>
        <button
          type="button"
          disabled={!impact || !matches || busy}
          onClick={confirm}
          className="flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:bg-gray-200 disabled:text-gray-500"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Decommission permanently
        </button>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* History                                                             */
/* ------------------------------------------------------------------ */

const ACTION_LABEL = {
  "in_house.create": "Created",
  "in_house.update": "Renamed or described",
  "in_house.define_api": "Changed address or sign-in",
  "in_house.import": "Imported calls",
  "in_house.connection.save": "Saved sign-in details",
  "in_house.endpoint.create": "Added a call",
  "in_house.endpoint.update": "Edited a call",
  "in_house.endpoint.delete": "Deleted a call",
  "in_house.publish": "Published",
  "in_house.unpublish": "Unpublished",
  "in_house.disable": "Disabled",
  "in_house.enable": "Turned back on",
  "in_house.decommission": "Decommissioned",
  "in_house.endpoint.publish": "Published a call",
  "in_house.endpoint.unpublish": "Unpublished a call",
  "provider.logo_upload": "Uploaded a logo",
  "provider.logo_from_site": "Used the site's icon as the logo"
};

/** What an audit entry did, in words; unknown actions show as recorded. */
export const historyLabel = (action) => ACTION_LABEL[action] || action;

const PAGE = 100;

/** "Today", "Yesterday" or the date, in the viewer's locale. */
function dayLabel(iso, now = new Date()) {
  const date = new Date(iso);
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(date)) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return date.toLocaleDateString(undefined, { dateStyle: "medium" });
}

/** Entries grouped by day, newest first, as GitHub and Linear show activity. */
export function byDay(rows, now = new Date()) {
  const groups = new Map();
  rows.forEach((r) => {
    const day = dayLabel(r.created_at, now);
    groups.set(day, [...(groups.get(day) || []), r]);
  });
  return [...groups.entries()];
}

/** One entry's before/after copy, closed until asked for: imports and saves can be large. */
function HistoryDiff({ diff, name }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-xs font-medium text-blue-700 hover:underline">
        {open ? "Hide what changed" : "Show what changed"}
      </button>
      {open && (
        <div className="mt-2">
          <ResponseViewer body={diff} devMode name={name} allowTable={false} />
        </div>
      )}
    </div>
  );
}

export function HistoryPanel({ integration, devMode }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // Filtered on the server, so older entries aren't lost behind other integrations' ones,
  // and deleted calls and sign-in changes still show.
  const load = async (offset) => {
    setLoading(true);
    setError("");
    const result = await getAuditTrail(integration.id, { limit: PAGE, offset });
    setLoading(false);
    if (!result.success) {
      setError(result.message);
      return;
    }
    setRows((r) => (offset ? [...(r || []), ...result.data] : result.data));
    setMore(result.data.length === PAGE);
  };

  useEffect(() => {
    load(0);
    // Reload when the integration itself changes, not on every refresh of its fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration.id, integration.updated_at]);

  return (
    <Card title="History" subtitle="Who changed what. Each entry keeps a before/after copy.">
      <ErrorNote message={error} />
      {!rows && !error && <OrbitLoader size={28} label="Loading history" />}
      {rows && rows.length === 0 && <p className="text-sm text-gray-500">No changes recorded yet.</p>}
      {rows && rows.length > 0 && (
        <div className="space-y-6">
          {byDay(rows).map(([day, entries]) => (
            <section key={day} aria-label={day}>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{day}</h4>
              <ol className="divide-y divide-gray-100 rounded-xl border border-gray-200">
                {entries.map((r) => (
                  <li key={r.id} className="flex gap-3 px-4 py-3">
                    <History className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-baseline justify-between gap-x-4 text-sm text-gray-900">
                        <span className="min-w-0">
                          <span className="font-medium">{historyLabel(r.action)}</span>
                          {r.summary ? <span className="text-gray-600"> · {r.summary}</span> : ""}
                        </span>
                        <span className="shrink-0 text-xs text-gray-500">
                          {r.actor_email || "Someone"} · {new Date(r.created_at).toLocaleTimeString(undefined, { timeStyle: "short" })}
                        </span>
                      </p>
                      {devMode && r.diff && <HistoryDiff diff={r.diff} name={`${r.action}-${r.id}`} />}
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
      )}
      {more && (
        <button type="button" disabled={loading} onClick={() => load(rows.length)} className="mt-5 flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Show older changes
        </button>
      )}
    </Card>
  );
}
